import {
  CUSTOM_VARS,
  DEFAULT_CUSTOM_THEME,
  customThemeVars,
  type CustomTheme,
} from "@/lib/custom-theme";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "system";

/** Colour family, independent of light/dark. "teal" is the Somasi default. */
export const PALETTES = ["teal", "ocean", "royal", "forest", "graphite", "custom"] as const;
export type Palette = (typeof PALETTES)[number];

const STORAGE_KEY = "somasi-theme";
const PALETTE_KEY = "somasi-palette";
const CUSTOM_KEY = "somasi-custom-theme";
/* The derived variables for both modes, so index.html can paint them before React loads. */
const CUSTOM_VARS_KEY = "somasi-custom-vars";

type ThemeState = {
  /** What the user picked — "system" means follow the OS. */
  theme: Theme;
  /** What is actually painted right now, with "system" already resolved. */
  resolved: "light" | "dark";
  setTheme: (t: Theme) => void;
  palette: Palette;
  setPalette: (p: Palette) => void;
  /** The user's own theme — only painted while `palette` is "custom". */
  customTheme: CustomTheme;
  setCustomTheme: (t: CustomTheme) => void;
};

const ThemeContext = createContext<ThemeState | undefined>(undefined);

function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // Private mode or blocked storage — fall back to following the OS.
  }
  return "system";
}

function readStoredPalette(): Palette {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    if (PALETTES.includes(v as Palette)) return v as Palette;
  } catch {
    // Same as above — default palette is fine.
  }
  return "teal";
}

function readStoredCustom(): CustomTheme {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<CustomTheme>;
      if (typeof v.color === "string" && /^#[0-9a-f]{6}$/i.test(v.color)) {
        return {
          name: typeof v.name === "string" && v.name.trim() ? v.name : DEFAULT_CUSTOM_THEME.name,
          color: v.color,
          logo: typeof v.logo === "string" ? v.logo : null,
        };
      }
    }
  } catch {
    // Corrupt or blocked storage — start from the default custom theme.
  }
  return DEFAULT_CUSTOM_THEME;
}

function prefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [palette, setPaletteState] = useState<Palette>(readStoredPalette);
  const [customTheme, setCustomThemeState] = useState<CustomTheme>(readStoredCustom);
  const [systemDark, setSystemDark] = useState(prefersDark);

  // "system" has to keep tracking the OS after the first paint, not just read
  // it once — the user can flip their appearance while the tab is open.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved: "light" | "dark" = theme === "system" ? (systemDark ? "dark" : "light") : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolved === "dark");
    root.style.colorScheme = resolved;
  }, [resolved]);

  useEffect(() => {
    const root = document.documentElement;
    if (palette === "teal") delete root.dataset.palette;
    else root.dataset.palette = palette;
  }, [palette]);

  // A custom theme has no stylesheet block; its tokens go on <html> inline and
  // are cleared again as soon as another palette is picked.
  useEffect(() => {
    const style = document.documentElement.style;
    if (palette !== "custom") {
      for (const k of CUSTOM_VARS) style.removeProperty(k);
      return;
    }
    for (const [k, v] of Object.entries(customThemeVars(customTheme.color, resolved))) {
      style.setProperty(k, v);
    }
    try {
      localStorage.setItem(
        CUSTOM_VARS_KEY,
        JSON.stringify({
          light: customThemeVars(customTheme.color, "light"),
          dark: customThemeVars(customTheme.color, "dark"),
        }),
      );
    } catch {
      // Only costs a flash of the default palette on the next reload.
    }
  }, [palette, customTheme.color, resolved]);

  // Mobile browsers tint their toolbar with theme-color; keep it on the active
  // palette and mode instead of the fixed teal in index.html.
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    meta.content = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  }, [palette, resolved, customTheme.color]);

  const setCustomTheme = useCallback((t: CustomTheme) => {
    setCustomThemeState(t);
    try {
      localStorage.setItem(CUSTOM_KEY, JSON.stringify(t));
    } catch {
      // Usually a logo too large for the quota; the theme still applies this tab.
    }
  }, []);

  const setPalette = useCallback((p: Palette) => {
    setPaletteState(p);
    try {
      localStorage.setItem(PALETTE_KEY, p);
    } catch {
      // Not persisting is survivable; the choice still applies for this tab.
    }
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // Not persisting is survivable; the choice still applies for this tab.
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{ theme, resolved, setTheme, palette, setPalette, customTheme, setCustomTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside a ThemeProvider");
  return ctx;
}
